import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import {
  Modal,
  ModalBody,
  Button,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import moment from "moment";
import TimePicker from "rc-time-picker";

import { constructMomentFromTime } from "utils";
import crossIcon from "../../../../../assets/images/cross.svg";
import Text from "components/Text";
import Table from "components/table";
import ThreeDots from "../../../../../assets/images/threedots.svg";
import "../StatusDetails.scss";
import styles from "../../StaffAttendanceTable.module.scss";
import constants from "../../../../../constants";
import FamilyModal from "patient-scheduling/pages/FamilyMembers/components/AddedMembers/FamilyModal";
import RejectionModal from "staff/pages/StaffListingTimesheet/component/RejectionModal";
import {
  updateTimesheetStatus,
  updateTimesheet,
  useGlobalTimesheetReport,
} from "repositories/timesheet-repository";
import toast from "react-hot-toast";
import Loader from "components/Loader";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";

const ViewDetailsModal = ({
  isOpen,
  toggle,
  staff,
  date,
  t,
  refetch,
  showLoader,
  setShowLoader,
}) => {
  const timesheetListingStatus = constants.TimesheetListingStatus;
  const [selectedTimesheetIds, setSelectedTimesheetIds] = useState([]);
  const [confirmModal, setConfirmModal] = useState({ open: false, type: null });
  const [isRejectionModalOpen, setIsRejectionModalOpen] = useState(false);
  const [selectMemberName, setSelectMemberName] = useState("");
  const [editingRowId, setEditingRowId] = useState(null);
  const [editValues, setEditValues] = useState({
    startTime: "",
    break: "",
    endTime: "",
  });

  // Store edit values per row to prevent input focus loss
  const [rowEditValues, setRowEditValues] = useState({});

  // Get owner ID from context
  const { selectedId } = useAccountOwnerCommon();

  // Fetch timesheet details from API using GlobalTimesheetReport
  const userId = staff?.user?.id;
  const dateStr = date ? moment(date).format("YYYY-MM-DD") : null;

  // Fetch timesheet data using the same API as parent component
  const {
    data,
    isLoading,
    isFetching,
    refetch: timesheetRefetch,
  } = useGlobalTimesheetReport(
    [], // OfficeIds - empty to get all offices
    selectedId, // OwnerId
    dateStr, // StartDate
    dateStr, // EndDate (same as StartDate for single day)
    null, // SortBy
    { enabled: !!selectedId && !!userId && !!dateStr }, // options - only fetch when we have all required data
    userId ? [userId] : [], // UserIds - filter for this specific staff member
    [], // DesignationIds
    true, // IsActiveInclude
    false, // IsDeleteInclude
    false // IsInActiveInclude
  );

  // Extract timesheets for the specific date and staff from API response
  const fetchedTimesheets = useMemo(() => {
    if (!data?.data || !userId || !dateStr) return [];

    // Find the staff member in the response
    const staffData = data.data.staff?.find((s) => s?.user?.id === userId);
    if (!staffData || !staffData.timesheets) return [];

    // Filter timesheets for the specific date
    return staffData.timesheets.filter((ts) => {
      const tsDate = moment(ts.date).format("YYYY-MM-DD");
      return tsDate === dateStr;
    });
  }, [data, userId, dateStr]);

  // Reset editing state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setEditingRowId(null);
      setEditValues({
        startTime: "",
        break: "",
        endTime: "",
      });
      setRowEditValues({});
    }
  }, [isOpen]);

  const performUpdateStatus = useCallback(
    async (statusId, reason) => {
      if (showLoader) return;
      setShowLoader && setShowLoader(true);
      try {
        const response = await updateTimesheetStatus({
          timesheetId: selectedTimesheetIds?.toString(),
          statusId,
          reason: reason || "",
        });
        toast.success(response?.message);
        refetch && refetch();
      } catch (error) {
        toast.error(error?.message);
      } finally {
        setShowLoader && setShowLoader(false);
        setSelectedTimesheetIds([]);
      }
    },
    [selectedTimesheetIds, showLoader]
  );
  // Format time from datetime string
  const formatTime = (dateTimeStr) => {
    return dateTimeStr ? moment(dateTimeStr).format("h:mm A") : "--";
  };

  // Format break duration from minutes to decimal hours
  const formatBreakDuration = (minutes) => {
    if (!minutes || minutes === 0) return "--";
    // Convert minutes to decimal hours and round to 2 decimal places
    // Use parseFloat to remove trailing zeros if needed, then toFixed(2) to ensure 2 decimals
    const decimalHours = parseFloat((minutes / 60).toFixed(2));
    return `${decimalHours.toFixed(2)} hrs`;
  };

  // Parse break duration from string (e.g., "45 min", "1h 30m", "2h")
  const parseBreakDuration = (breakStr) => {
    if (!breakStr || breakStr === "--") return 0;
    const str = breakStr.trim().toLowerCase();
    let totalMinutes = 0;

    // Match hours (e.g., "1h", "2h")
    const hourMatch = str.match(/(\d+)\s*h/);
    if (hourMatch) {
      totalMinutes += parseInt(hourMatch[1]) * 60;
    }

    // Match minutes (e.g., "30m", "45 min")
    const minMatch = str.match(/(\d+)\s*m(?:in)?/);
    if (minMatch) {
      totalMinutes += parseInt(minMatch[1]);
    }

    return totalMinutes;
  };

  // Parse time string to moment object
  const parseTimeString = (timeStr) => {
    if (!timeStr || timeStr === "--") return null;
    // Try parsing with common formats
    const formats = ["h:mm A", "hh:mm A", "H:mm", "HH:mm"];
    for (const format of formats) {
      const parsed = moment(timeStr, format, true);
      if (parsed.isValid()) {
        return parsed;
      }
    }
    return null;
  };

  // Calculate time spent in minutes from start and end times
  const calculateTimeSpent = (startTimeStr, endTimeStr, dateStr) => {
    const start = parseTimeString(startTimeStr);
    const end = parseTimeString(endTimeStr);

    if (!start || !end) return 0;

    // Create full datetime objects using the timesheet date
    const date = moment(dateStr);
    const startDateTime = moment(date)
      .hour(start.hour())
      .minute(start.minute());
    const endDateTime = moment(date).hour(end.hour()).minute(end.minute());

    // Handle case where end time is next day
    if (endDateTime.isBefore(startDateTime)) {
      endDateTime.add(1, "day");
    }

    return endDateTime.diff(startDateTime, "minutes");
  };

  // Handle edit start
  const handleEditStart = useCallback((row) => {
    // Parse timesheet data to moment objects for TimePicker
    const timesheet = row.timesheet;
    let startTimeMoment = null;
    let endTimeMoment = null;

    // Parse start time - try multiple formats
    if (timesheet?.startedOn) {
      startTimeMoment = moment(timesheet.startedOn);
      if (!startTimeMoment.isValid()) {
        // Try parsing from the formatted string
        startTimeMoment = moment(row.startTime, "h:mm A", true);
      }
    } else if (row.startTime && row.startTime !== "--") {
      startTimeMoment = moment(row.startTime, "h:mm A", true);
    }

    // Parse end time - try multiple formats
    if (timesheet?.finishedOn) {
      endTimeMoment = moment(timesheet.finishedOn);
      if (!endTimeMoment.isValid()) {
        // Try parsing from the formatted string
        endTimeMoment = moment(row.endTime, "h:mm A", true);
      }
    } else if (row.endTime && row.endTime !== "--") {
      endTimeMoment = moment(row.endTime, "h:mm A", true);
    }

    // Parse break duration - convert minutes to hours:minutes for TimePicker
    const breakMinutes = timesheet?.breakDuration || timesheet?.breakTime || 0;
    const breakHours = Math.floor(breakMinutes / 60);
    const breakMins = breakMinutes % 60;
    const breakMoment =
      breakMinutes > 0
        ? constructMomentFromTime(breakHours, breakMins)
        : constructMomentFromTime(0, 0);

    // For display, convert to decimal hours
    const breakDecimalHours = breakMinutes
      ? (breakMinutes / 60).toFixed(2)
      : "";

    const initialValues = {
      startTime: row.startTime || "",
      break: breakDecimalHours || "",
      endTime: row.endTime || "",
    };

    const initialMomentValues = {
      startTime:
        startTimeMoment && startTimeMoment.isValid() ? startTimeMoment : null,
      endTime: endTimeMoment && endTimeMoment.isValid() ? endTimeMoment : null,
      break: breakMoment,
    };

    setEditValues(initialValues);
    setRowEditValues((prev) => ({
      ...prev,
      [row.id]: {
        ...initialValues,
        momentValues: initialMomentValues,
      },
    }));
    setEditingRowId(row.id);
  }, []);

  // Handle edit cancel
  const handleEditCancel = useCallback(() => {
    setEditingRowId(null);
    setEditValues({
      startTime: "",
      break: "",
      endTime: "",
    });
    setRowEditValues((prev) => {
      const newValues = { ...prev };
      if (editingRowId) {
        delete newValues[editingRowId];
      }
      return newValues;
    });
  }, [editingRowId]);

  // Handle edit save
  const handleEditSave = useCallback(
    async (row) => {
      if (showLoader) return;

      // Get the current edit values for this row
      const rowData = rowEditValues[row.id] || {};
      const currentEditValues = rowData.momentValues || {};

      // Get moment objects directly from rowEditValues
      const startTimeMoment = currentEditValues.startTime;
      const endTimeMoment = currentEditValues.endTime;
      const breakMoment =
        currentEditValues.break || constructMomentFromTime(0, 0);

      // Calculate break duration in minutes from moment object
      const breakDurationInMinutes = breakMoment
        ? breakMoment.hours() * 60 + breakMoment.minutes()
        : 0;

      // Validate inputs
      if (!startTimeMoment || !endTimeMoment) {
        toast.error("Start Time and End Time are required");
        return;
      }

      const startTime = startTimeMoment;
      const endTime = endTimeMoment;

      // Validate end time is after start time on the same day
      // Compare time values directly (similar to Form.jsx validation)
      const startTimeMinutes = startTime.hour() * 60 + startTime.minute();
      const endTimeMinutes = endTime.hour() * 60 + endTime.minute();

      if (endTimeMinutes <= startTimeMinutes) {
        toast.error("End Time must be after Start Time");
        return;
      }

      // Create full datetime objects using the timesheet date
      const dateMoment = moment(date);
      const startDateTime = moment(dateMoment)
        .hour(startTime.hour())
        .minute(startTime.minute())
        .second(0)
        .millisecond(0);
      let endDateTime = moment(dateMoment)
        .hour(endTime.hour())
        .minute(endTime.minute())
        .second(0)
        .millisecond(0);

      // Handle overnight shifts (end time on next day)
      // This should only happen if explicitly set, but we'll allow it
      if (endDateTime.isBefore(startDateTime)) {
        endDateTime = endDateTime.add(1, "day");
      }

      // Break duration in minutes is already calculated above

      // Calculate time spent in minutes (same as Form.jsx)
      const duration = moment.duration(endDateTime.diff(startDateTime));
      const totalTimeSpentInMinute = parseInt(duration.asMinutes());

      // Validate break time should be less than total time
      if (breakDurationInMinutes > totalTimeSpentInMinute) {
        toast.error("Break time should be less than total time.");
        return;
      }

      const timeSpentInMinutes =
        totalTimeSpentInMinute - breakDurationInMinutes;

      setShowLoader && setShowLoader(true);
      try {
        const timesheet = row.timesheet;

        // Format datetime strings exactly like Form.jsx: "YYYY-MM-DDTHH:mm"
        const startDateTimeStr = startDateTime.format("YYYY-MM-DDTHH:mm");
        const endDateTimeStr = endDateTime.format("YYYY-MM-DDTHH:mm");

        await updateTimesheet(
          {
            userId: timesheet.userId || staff?.user?.id,
            officeId: timesheet.officeId || timesheet.office?.id,
            timesheetId: timesheet.id,
            statusId: timesheet.statusId,
            date: moment(date).format("YYYY-MM-DDTHH:mm"), // Same format as Form.jsx
            startDateTime: startDateTimeStr, // Format: "YYYY-MM-DDTHH:mm"
            endDateTime: endDateTimeStr, // Format: "YYYY-MM-DDTHH:mm"
            timeSpentInMinutes: timeSpentInMinutes,
            breakDurationInMinutes: breakDurationInMinutes,
            ip: null,
            IsTypeAdvance: timesheet.isTypeAdvance || false,
            AdvanceTimesheetType: timesheet.advanceTimesheetType || 1,
            TimesheetTasks: timesheet.timesheetTasks || [],
            currentDate: moment().format("YYYY-MM-DDTHH:mm"), // Same format as Form.jsx
            note: timesheet.note || "",
          },
          true // isAdmin
        );

        toast.success("Timesheet updated successfully");
        setEditingRowId(null);
        setEditValues({
          startTime: "",
          break: "",
          endTime: "",
        });
        setRowEditValues((prev) => {
          const newValues = { ...prev };
          if (row.id) {
            delete newValues[row.id];
          }
          return newValues;
        });

        timesheetRefetch();
        if (refetch) {
          await refetch();
        }
      } catch (error) {
        toast.error(error?.message || "Failed to update timesheet");
      } finally {
        setShowLoader && setShowLoader(false);
      }
    },
    [
      editValues,
      rowEditValues,
      staff,
      date,
      showLoader,
      setShowLoader,
      refetch,
      toggle,
    ]
  );

  // Get status label and class from statusId
  const getStatusInfo = (statusId) => {
    switch (statusId) {
      case timesheetListingStatus[1]?.value: // Pending For Approval (2)
        return { label: "PENDING", class: "pending" };
      case timesheetListingStatus[2]?.value: // Approved (3)
        return { label: "APPROVED", class: "approved" };
      case timesheetListingStatus[3]?.value: // Rejected (4)
        return { label: "REJECTED", class: "rejected" };
      case timesheetListingStatus[4]?.value: // Paid (5)
        return { label: "PAID", class: "paid" };
      default:
        return { label: "PENDING", class: "pending" };
    }
  };

  // Check if date is in leave range
  const isDateInLeaveRange = (date, leaves) => {
    if (!leaves || leaves.length === 0) return false;
    return leaves.some((leave) => {
      const leaveStartDate = moment(leave.startDate).startOf("day");
      const leaveEndDate = moment(leave.endDate).startOf("day");
      const checkDate = moment(date).startOf("day");
      return checkDate.isBetween(leaveStartDate, leaveEndDate, "day", "[]");
    });
  };

  // Process timesheet data for table
  const tableData = useMemo(() => {
    if (!staff || !date) return [];

    const timesheets = fetchedTimesheets;
    const result = [];

    // Use staff data from API response if available, otherwise use prop
    const staffData =
      data?.data?.staff?.find((s) => s?.user?.id === userId) || staff;

    // Get all leaves that cover this date
    const leavesForDate =
      staffData?.leaves?.filter((leave) => {
        const leaveStartDate = moment(leave.startDate).startOf("day");
        const leaveEndDate = moment(leave.endDate).startOf("day");
        const checkDate = moment(date).startOf("day");
        return checkDate.isBetween(leaveStartDate, leaveEndDate, "day", "[]");
      }) || [];

    // Add leave entries (show all leaves for this date, even if timesheets exist)
    leavesForDate.forEach((leave, index) => {
      result.push({
        id: `leave-${date}-${index}`,
        officeName: leave?.office?.name || leave?.officeName || "N/A",
        startTime: "--",
        break: "--",
        endTime: "--",
        status: "LEAVE",
        statusClass: "leave",
        statusId: null,
        isLeave: true,
        leave: leave, // Store full leave for reference
      });
    });

    // Add timesheet entries
    if (timesheets && timesheets.length > 0) {
      timesheets.forEach((timesheet, index) => {
        const statusInfo = getStatusInfo(timesheet.statusId);

        result.push({
          id: timesheet.id || `ts-${index}`,
          officeName: timesheet.office?.name || timesheet.officeName || "N/A",
          startTime: formatTime(timesheet.startedOn),
          break: formatBreakDuration(
            timesheet.breakDuration || timesheet.breakTime
          ),
          endTime: formatTime(timesheet.finishedOn),
          status: statusInfo.label,
          statusClass: statusInfo.class,
          statusId: timesheet.statusId,
          timesheet: timesheet, // Store full timesheet for actions
          isLeave: false,
        });
      });
    }

    return result;
  }, [staff, date, fetchedTimesheets, data, userId, timesheetListingStatus]);

  // Get staff name and date for header
  const staffName = staff?.user
    ? `${staff.user.firstName || ""} ${staff.user.lastName || ""}`.trim()
    : "";

  const dateLabel = date ? moment(date).format("ddd DD, MMM") : "";

  const columns = useMemo(
    () => [
      {
        attrs: { datatitle: "Office Name" },
        dataField: "officeName",
        text: "Office Name",
        formatter: (cell) => <span>{cell}</span>,
      },
      {
        attrs: { datatitle: "Start Time" },
        dataField: "startTime",
        text: "Start Time",
        formatter: (cell, row) => {
          if (editingRowId === row.id) {
            // Access current values - will trigger re-render when rowEditValues changes
            const rowData = rowEditValues[row.id] || {};
            const momentValue = rowData.momentValues?.startTime || null;

            return (
              <TimePicker
                key={`start-${row.id}`}
                showSecond={false}
                placeholder="--"
                format="h:mm A"
                value={momentValue}
                onChange={(momentObj) => {
                  const timeString = momentObj
                    ? momentObj.format("h:mm A")
                    : "";
                  setRowEditValues((prev) => ({
                    ...prev,
                    [row.id]: {
                      ...(prev[row.id] || {}),
                      startTime: timeString,
                      momentValues: {
                        ...(prev[row.id]?.momentValues || {}),
                        startTime: momentObj,
                      },
                    },
                  }));
                  setEditValues((prev) => ({
                    ...prev,
                    startTime: timeString,
                  }));
                }}
                use12Hours
                style={{ width: "120px" }}
                popupClassName={styles["time-picker-popup"]}
                className={styles["time-picker"]}
              />
            );
          }
          return <span>{cell}</span>;
        },
      },
      {
        attrs: { datatitle: "Break" },
        dataField: "break",
        text: "Break",
        formatter: (cell, row) => {
          if (editingRowId === row.id) {
            // Always calculate from timesheet data (source of truth)
            // This ensures we always have the correct value regardless of rowEditValues state
            let momentValue = null;

            if (row.timesheet) {
              const breakMinutes =
                row.timesheet?.breakDuration || row.timesheet?.breakTime || 0;
              const breakHours = Math.floor(breakMinutes / 60);
              const breakMins = breakMinutes % 60;

              momentValue =
                breakMinutes > 0
                  ? constructMomentFromTime(breakHours, breakMins)
                  : constructMomentFromTime(0, 0);
            }

            // Get current edit values - use if user has made changes
            const rowData = rowEditValues[row.id] || {};
            if (rowData.momentValues?.break) {
              // Use user's changed value if it exists
              momentValue = rowData.momentValues.break;
            }

            // Fallback to 0 if still not set
            if (!momentValue) {
              momentValue = constructMomentFromTime(0, 0);
            }

            return (
              <TimePicker
                key={`break-${row.id}-${editingRowId}`}
                showSecond={false}
                placeholder="--"
                format="HH [hr] mm [min]"
                value={momentValue}
                onChange={(momentObj) => {
                  if (!momentObj) {
                    // If cleared, set to 0
                    momentObj = constructMomentFromTime(0, 0);
                  }
                  const hours = momentObj.hours();
                  const minutes = momentObj.minutes();
                  // Convert to decimal hours for display
                  const totalMinutes = hours * 60 + minutes;
                  const decimalHours =
                    totalMinutes > 0 ? (totalMinutes / 60).toFixed(2) : "";

                  setRowEditValues((prev) => ({
                    ...prev,
                    [row.id]: {
                      ...(prev[row.id] || {}),
                      break: decimalHours,
                      momentValues: {
                        ...(prev[row.id]?.momentValues || {}),
                        break: momentObj,
                      },
                    },
                  }));
                  setEditValues((prev) => ({
                    ...prev,
                    break: decimalHours,
                  }));
                }}
                disabledHours={() => new Array(16).fill(0).map((v, i) => i + 9)}
                hideDisabledOptions={true}
                style={{ width: "120px" }}
                popupClassName={styles["time-picker-popup"]}
                className={styles["time-picker"]}
              />
            );
          }
          return <span>{cell}</span>;
        },
      },
      {
        attrs: { datatitle: "End Time" },
        dataField: "endTime",
        text: "End Time",
        formatter: (cell, row) => {
          if (editingRowId === row.id) {
            // Access current values from closure - this will have latest values
            const currentRowEditValues = rowEditValues; // Capture current value
            const rowData = currentRowEditValues[row.id] || {};
            const momentValue = rowData.momentValues?.endTime || null;

            return (
              <TimePicker
                key={`end-${row.id}`}
                showSecond={false}
                placeholder="--"
                format="h:mm A"
                value={momentValue}
                onChange={(momentObj) => {
                  const timeString = momentObj
                    ? momentObj.format("h:mm A")
                    : "";
                  setRowEditValues((prev) => ({
                    ...prev,
                    [row.id]: {
                      ...(prev[row.id] || {}),
                      endTime: timeString,
                      momentValues: {
                        ...(prev[row.id]?.momentValues || {}),
                        endTime: momentObj,
                      },
                    },
                  }));
                  setEditValues((prev) => ({
                    ...prev,
                    endTime: timeString,
                  }));
                }}
                use12Hours
                style={{ width: "120px" }}
                popupClassName={styles["time-picker-popup"]}
                className={styles["time-picker"]}
              />
            );
          }
          return <span>{cell}</span>;
        },
      },
      {
        attrs: { datatitle: "Status" },
        dataField: "status",
        text: "Status",
        formatter: (cell, row) => (
          <Button className={`appointment_status_btn ${row.statusClass}`}>
            {row.status}
          </Button>
        ),
      },
      {
        attrs: { datatitle: "Action" },
        dataField: "action",
        text: "Actions",
        formatter: (cell, row) => {
          // Don't show actions for leave entries
          if (row.isLeave) return null;

          // Don't show actions if statusId is not set
          if (row.statusId === null || row.statusId === undefined) return null;

          const menuOptions = [];
          const statusId = Number(row.statusId); // Ensure statusId is a number for comparison

          // Add "Mark as Paid" for Approved status (statusId = 3)
          if (statusId === timesheetListingStatus[2]?.value) {
            menuOptions.push({
              text: "Mark as Paid",
              onClick: () => {
                setSelectedTimesheetIds([row.timesheet.id]);
                setConfirmModal({ open: true, type: "paid" });
              },
            });
          }

          // Add "Approve" and "Reject" for Pending status (statusId = 2)
          if (statusId === timesheetListingStatus[1]?.value) {
            menuOptions.push({
              text: t("staffAttendance.approve"),
              onClick: () => {
                setSelectedTimesheetIds([row.timesheet.id]);
                setConfirmModal({ open: true, type: "approve" });
              },
            });
            menuOptions.push({
              text: "Reject",
              onClick: () => {
                setSelectedTimesheetIds([row.timesheet.id]);
                setSelectMemberName(
                  `${staff?.user?.firstName || ""} ${
                    staff?.user?.lastName || ""
                  }`
                );
                setIsRejectionModalOpen(true);
              },
            });
          }

          // Add "Edit" for all statuses except Paid (statusId = 5)
          if (statusId !== timesheetListingStatus[4]?.value) {
            menuOptions.push({
              text: "Edit",
              onClick: () => {
                handleEditStart(row);
              },
            });
          }

          // If this row is being edited, show Save and Cancel buttons instead of dropdown
          if (editingRowId === row.id) {
            return (
              <div style={{ display: "flex", gap: "8px" }}>
                <span
                  className="link-btn"
                  onClick={() => handleEditSave(row)}
                  style={{ cursor: "pointer" }}
                >
                  {t("save")}
                </span>
                <span
                  className="link-btn"
                  onClick={handleEditCancel}
                  style={{ cursor: "pointer" }}
                >
                  {t("cancel")}
                </span>
              </div>
            );
          }

          // Only show dropdown if there are menu options
          if (menuOptions.length === 0) return null;

          return (
            <UncontrolledDropdown>
              <DropdownToggle
                caret={false}
                tag="div"
                className="cursor-pointer"
              >
                <img src={ThreeDots} alt="menu" />
              </DropdownToggle>
              <DropdownMenu end className={styles["office-dropdown-menu"]}>
                {menuOptions.map((option, index) => (
                  <DropdownItem
                    key={index}
                    onClick={option.onClick}
                    className={styles["office-dropdown-item"]}
                  >
                    {option.text}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [
      editingRowId,
      staff,
      date,
      timesheetListingStatus,
      handleEditStart,
      handleEditSave,
      handleEditCancel,
      // Note: rowEditValues is intentionally NOT in dependencies to prevent remounting
      // The formatters access rowEditValues from closure, which will have the latest values
    ]
  );

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      className="modal-dialog-centered new-group-modal view_details_modal_width"
      modalClassName="custom-modal"
    >
      <span className="close-btn" onClick={toggle}>
        <img src={crossIcon} alt="close" />
      </span>

      <ModalBody>
        {(isLoading || isFetching) && <Loader />}
        <div className="mb-4">
          <Text size="25px" weight="500" color="#111B45">
            {staffName || "N/A"}
          </Text>
          <Text size="14px" weight="300" color="#000000">
            {dateLabel || ""}
          </Text>
        </div>

        <div className="view_details_table">
          {tableData.length > 0 ? (
            <Table
              key={`table-${editingRowId || "none"}-${JSON.stringify(
                rowEditValues
              )}`}
              keyField="id"
              data={tableData}
              columns={columns}
              bordered={false}
            />
          ) : (
            <div className="text-center p-4">
              <Text size="14px" weight="400" color="#666">
                {t("staffAttendance.noTimesheetDataAvailable")}
              </Text>
            </div>
          )}
        </div>
        {confirmModal.open && (
          <FamilyModal
            isFamilyModalOpen={confirmModal.open}
            setIsFamilyModalOpen={(val) =>
              setConfirmModal((prev) => ({ ...prev, open: val }))
            }
            title={
              confirmModal.type === "approve"
                ? t("staffAttendance.approveTimesheet")
                : t("staffAttendance.markTimesheetAsPaid")
            }
            subTitle1={
              confirmModal.type === "approve"
                ? t("staffAttendance.areYouSureYouWantToApproveTheTimesheet")
                : t("staffAttendance.areYouSureYouWantToMarkTheTimesheetAsPaid")
            }
            leftBtnText={
              confirmModal.type === "approve"
                ? t("staffAttendance.approve")
                : t("staffAttendance.markAsPaid")
            }
            rightBtnText={t("cancel")}
            onConfirm={async () => {
              const statusId =
                confirmModal.type === "approve"
                  ? timesheetListingStatus[2]?.value
                  : timesheetListingStatus[4]?.value;
              setConfirmModal({ open: false, type: null });
              await performUpdateStatus(statusId);
              toggle();
            }}
          />
        )}
        {isRejectionModalOpen && (
          <RejectionModal
            isRejectionModalOpen={isRejectionModalOpen}
            setIsRejectionModalOpen={setIsRejectionModalOpen}
            staffMemberName={selectMemberName}
            hideSelectedEmployee={true}
            titleOverride={t("staffAttendance.rejectTimesheet")}
            confirmLabelOverride={t("staffAttendance.reject")}
            confirmMessage={t(
              "staffAttendance.areYouSureYouWantToRejectTheTimesheet"
            )}
            centerTitle={true}
            onReject={async (reason) => {
              setIsRejectionModalOpen(false);
              await performUpdateStatus(
                timesheetListingStatus[3]?.value,
                reason
              );
              toggle();
            }}
          />
        )}
      </ModalBody>
    </Modal>
  );
};

export default ViewDetailsModal;
